import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  CircleAlert,
  ExternalLink,
  History,
  Info,
  Loader2,
  Lock,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PolicyOptionCard } from '@shared/ai-data-policy/components/PolicyOptionCard';
import { PolicySummaryCard } from '@shared/ai-data-policy/components/PolicySummaryCard';
import { AiDataPolicyForbiddenError } from '@shared/ai-data-policy/errors';
import { formatPolicyTime, policyValueKey } from '@shared/ai-data-policy/labels';
import { useAiDataPolicyLiveRefresh } from '@shared/ai-data-policy/live';
import { bidiIsolate } from '@shared/ai-data-policy/names';
import { useAiDataPolicyStore } from '@shared/ai-data-policy/store';
import {
  policyStamp,
  type AiDataPolicy,
  type AiDataPolicyChoice,
} from '@shared/ai-data-policy/types';
import { Button } from '@shared/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@shared/components/ui/Dialog';
import { cn } from '@shared/components/ui/cn';
import { useOnline } from '@shared/hooks/use-online';
import { useToastStore } from '@shared/stores/toast.store';

import { DATA_POLICY_HELP_URL, goToDataPolicySettings } from '../data-policy/navigation';
import { usePolicyPromptStore, type PromptRequest } from '../data-policy/prompt.store';
import { markCurrentPolicyNoteSeen } from '../data-policy/system-notes';
import type { DataCategory } from '../types';

type Phase = 'choose' | 'saving' | 'save_failed' | 'no_access' | 'elsewhere';

function lowerData(
  t: (key: string, fallback: string) => string,
  categories: DataCategory[],
): string {
  const investor = categories.includes('investor');
  const crm = categories.includes('crm');
  if (investor && crm) return t('answerDetails.data.both', 'investor and CRM data');
  if (crm) return t('answerDetails.data.crm', 'CRM data');
  return t('answerDetails.data.investor', 'investor data');
}

function DialogHead({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 flex-col gap-2 ps-6 pe-16 pt-6">{children}</div>;
}

function DialogScroll({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">{children}</div>
  );
}

function DialogActions({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 flex-wrap justify-end gap-2 px-6 pb-6">{children}</div>;
}

function NoteRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <span className="flex h-5 w-4 shrink-0 items-center justify-center text-muted-foreground">
        {icon}
      </span>
      <span className="text-sm text-muted-foreground">{children}</span>
    </li>
  );
}

function AlertBox({
  tone,
  icon,
  title,
  body,
}: {
  tone: 'error' | 'warning' | 'neutral';
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      role={tone === 'neutral' ? 'status' : 'alert'}
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        tone === 'error' &&
          'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
        tone === 'warning' &&
          'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
        tone === 'neutral' && 'border-border bg-card text-foreground',
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium">{title}</span>
        <span className={cn('text-sm', tone === 'neutral' && 'text-muted-foreground')}>{body}</span>
      </span>
    </div>
  );
}

function DataPolicyDialogBody({ request }: { request: PromptRequest }) {
  const { t, i18n } = useTranslation('ai-cio');
  const rtl = i18n.dir() === 'rtl';
  const { t: tp } = useTranslation('ai-data-policy');
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);
  const policy = useAiDataPolicyStore((s) => s.policy);
  const workspace = useAiDataPolicyStore((s) => s.workspace);
  const save = useAiDataPolicyStore((s) => s.save);
  const resolve = usePolicyPromptStore((s) => s.resolve);
  const online = useOnline();

  const [selected, setSelected] = useState<AiDataPolicyChoice | null>(null);
  const [phase, setPhase] = useState<Phase>('choose');
  const [validation, setValidation] = useState(false);
  const [elsewhere, setElsewhere] = useState<AiDataPolicy | null>(null);
  const [openedAt] = useState(() => Date.now());
  const savedByMe = useRef(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const paused = request.variant === 'paused';
  const manual = request.variant === 'manual';
  const model = request.selectedModel?.label ?? '';
  const provider = request.selectedModel?.providerLabel ?? '';
  const data = lowerData(t, request.categories);
  const peopleCount = workspace?.peopleCount ?? null;

  useAiDataPolicyLiveRefresh(15_000);
  useEffect(() => {
    void useAiDataPolicyStore.getState().loadWorkspace();
  }, []);

  const alreadySetToast = useCallback(
    (current: AiDataPolicy) => {
      addToast({
        variant: 'success',
        title: t('dataPolicy.toast.alreadySetTitle', 'Data policy already set'),
        description: t('dataPolicy.toast.alreadySetBody', '{{name}} chose {{value}} at {{time}}.', {
          name: current.setBy?.name
            ? bidiIsolate(current.setBy.name, rtl)
            : t('systemNote.someone', 'An admin'),
          value: tp(policyValueKey(current.value)),
          time: formatPolicyTime(current.setAt),
        }),
        duration: 6000,
      });
    },
    [addToast, t, tp, rtl],
  );

  useEffect(() => {
    if (!policy || policy.value === 'not_chosen' || savedByMe.current) return;
    if (phase === 'saving' || phase === 'elsewhere' || phase === 'no_access') return;
    if ((selected && selected === policy.value) || policyStamp(policy) < openedAt) {
      alreadySetToast(policy);
      resolve('elsewhere');
      return;
    }
    setElsewhere(policy);
    setPhase('elsewhere');
  }, [policy, phase, selected, openedAt, alreadySetToast, resolve]);

  const decideLater = () => {
    if (phase === 'saving') return;
    markCurrentPolicyNoteSeen();
    resolve('later');
  };

  const openSettings = () => {
    goToDataPolicySettings(navigate);
    resolve(phase === 'elsewhere' ? 'elsewhere' : 'later');
  };

  const submit = async () => {
    if (!selected) {
      setValidation(true);
      optionsRef.current?.querySelector<HTMLInputElement>('input[type="radio"]')?.focus();
      return;
    }
    setValidation(false);
    setPhase('saving');
    savedByMe.current = true;
    try {
      const result = await save({ value: selected, onlyIfUndecided: true });
      markCurrentPolicyNoteSeen();
      if (!result.changed) {
        if (result.policy.value === selected) {
          alreadySetToast(result.policy);
          resolve('elsewhere');
          return;
        }
        setElsewhere(result.policy);
        setPhase('elsewhere');
        return;
      }
      const value = tp(policyValueKey(result.policy.value));
      addToast({
        variant: 'success',
        title: t('dataPolicy.toast.savedTitle', 'Data policy saved'),
        description:
          peopleCount === null
            ? t(
                'dataPolicy.toast.savedEveryone',
                '{{value}} · applies to everyone in your workspace.',
                { value },
              )
            : t(
                'dataPolicy.toast.savedPeople',
                '{{value}} · applies to all {{count}} people in your workspace.',
                { value, count: peopleCount },
              ),
        duration: 8000,
        action: {
          label: t('dataPolicy.toast.viewInSettings', 'View in Settings'),
          onClick: () => {
            goToDataPolicySettings(navigate);
          },
        },
      });
      resolve('saved');
    } catch (err) {
      savedByMe.current = false;
      setPhase(err instanceof AiDataPolicyForbiddenError ? 'no_access' : 'save_failed');
    }
  };

  const saving = phase === 'saving';
  const primaryLabel = saving
    ? t('dataPolicy.saving', 'Saving…')
    : selected === 'investbanq_only'
      ? t('dataPolicy.saveInternal', 'Use Investbanq model only')
      : selected === 'commercial_allowed'
        ? t('dataPolicy.saveCommercial', 'Allow commercial models')
        : t('dataPolicy.save', 'Save choice');

  if (phase === 'elsewhere' && elsewhere) {
    const name = elsewhere.setBy?.name
      ? bidiIsolate(elsewhere.setBy.name, rtl)
      : t('systemNote.someone', 'An admin');
    const time = formatPolicyTime(elsewhere.setAt);
    return (
      <>
        <DialogHead>
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className="text-xl font-semibold leading-6 outline-none"
          >
            {t('dataPolicy.elsewhereTitle', '{{name}} already chose a data policy', { name })}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {paused
              ? t(
                  'dataPolicy.elsewhereBodyPaused',
                  '{{name}} chose it at {{time}}, while this was open. It applies to everyone in your workspace, including this request.',
                  { name, time },
                )
              : t(
                  'dataPolicy.elsewhereBody',
                  '{{name}} chose it at {{time}}, while this was open. It applies to everyone in your workspace.',
                  { name, time },
                )}
          </DialogDescription>
        </DialogHead>
        <DialogScroll>
          <PolicySummaryCard policy={elsewhere} />
          <ul className="flex flex-col gap-2">
            <NoteRow icon={<History className="size-4" aria-hidden />}>
              {selected
                ? t(
                    'dataPolicy.elsewhereNote',
                    'Your selection, {{value}}, wasn’t saved. You can change the policy in Settings → Company, and every change is logged.',
                    { value: tp(policyValueKey(selected)) },
                  )
                : t(
                    'dataPolicy.elsewhereNoteNoSelection',
                    'You can change the policy in Settings → Company, and every change is logged.',
                  )}
            </NoteRow>
          </ul>
        </DialogScroll>
        <DialogActions key="elsewhere">
          <Button variant="outline" onClick={openSettings}>
            {t('dataPolicy.openSettings', 'Open Settings')}
            <ArrowRight className="rtl:-scale-x-100" aria-hidden />
          </Button>
          <Button
            className="min-w-[232px]"
            onClick={() => {
              resolve('elsewhere');
            }}
          >
            {t('dataPolicy.continue', 'Continue')}
          </Button>
        </DialogActions>
      </>
    );
  }

  const noAccess = phase === 'no_access';
  const description = noAccess
    ? paused
      ? t(
          'dataPolicy.descriptionPausedPlain',
          'Your question involves {{data}}, and {{model}} ({{provider}}) is selected. Your request is paused, and nothing has been sent to {{provider}}.',
          { data, model, provider },
        )
      : t(
          'dataPolicy.descriptionPlain',
          'Which AI models can process investor and CRM data in your workspace.',
        )
    : paused
      ? request.investorName
        ? t(
            'dataPolicy.descriptionPaused',
            'Your question about {{investor}} involves investor data, and {{model}} ({{provider}}) is selected. Your request is paused, and nothing has been sent to {{provider}}. As an admin, you choose this for everyone in your workspace.',
            { investor: request.investorName, model, provider },
          )
        : t(
            'dataPolicy.descriptionPausedNoName',
            'Your question involves {{data}}, and {{model}} ({{provider}}) is selected. Your request is paused, and nothing has been sent to {{provider}}. As an admin, you choose this for everyone in your workspace.',
            { data, model, provider },
          )
      : manual
        ? t(
            'dataPolicy.descriptionManual',
            'As an admin, you choose which AI models can process investor and CRM data for everyone in your workspace.',
          )
        : t(
            'dataPolicy.descriptionRunning',
            'Your question involves {{data}}. It’s running on the Investbanq model, the model you selected, so your choice doesn’t affect this request. It sets what happens on new requests for everyone in your workspace, including you.',
            { data },
          );

  return (
    <>
      <DialogHead>
        <DialogTitle
          ref={titleRef}
          tabIndex={-1}
          className="text-xl font-semibold leading-6 outline-none"
        >
          {t('dataPolicy.title', 'Which AI models can process investor and CRM data?')}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          {description}
        </DialogDescription>
      </DialogHead>

      <DialogScroll>
        <div
          ref={optionsRef}
          role="radiogroup"
          aria-label={t('dataPolicy.title', 'Which AI models can process investor and CRM data?')}
          className="flex flex-col gap-4 md:flex-row"
        >
          <PolicyOptionCard
            name="ai-data-policy-choice"
            value="investbanq_only"
            checked={selected === 'investbanq_only'}
            disabled={saving || noAccess}
            onSelect={(value) => {
              setSelected(value);
              setValidation(false);
            }}
          />
          <PolicyOptionCard
            name="ai-data-policy-choice"
            value="commercial_allowed"
            checked={selected === 'commercial_allowed'}
            disabled={saving || noAccess}
            onSelect={(value) => {
              setSelected(value);
              setValidation(false);
            }}
          />
        </div>

        {validation && (
          <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
            <CircleAlert className="size-4 shrink-0" aria-hidden />
            {t('dataPolicy.validation', 'Choose one of the two options, or select Decide later.')}
          </p>
        )}

        <ul className="flex flex-col gap-2">
          <NoteRow icon={<Users className="size-4" aria-hidden />}>
            {peopleCount === null
              ? paused
                ? t(
                    'dataPolicy.noteEveryonePaused',
                    'Applies to this request and to new requests from everyone in your workspace.',
                  )
                : t(
                    'dataPolicy.noteEveryone',
                    'Applies to new requests from everyone in your workspace.',
                  )
              : paused
                ? t(
                    'dataPolicy.notePeoplePaused',
                    'Applies to this request and to new requests from all {{count}} people in your workspace.',
                    { count: peopleCount },
                  )
                : t(
                    'dataPolicy.notePeople',
                    'Applies to new requests from all {{count}} people in your workspace.',
                    { count: peopleCount },
                  )}
          </NoteRow>
          {!noAccess && (
            <>
              <NoteRow icon={<Info className="size-4" aria-hidden />}>
                {manual
                  ? t(
                      'dataPolicy.noteDefault',
                      'Until an admin chooses, only the Investbanq model processes investor and CRM data.',
                    )
                  : paused
                    ? t(
                        'dataPolicy.noteLaterPaused',
                        'If you decide later, this request uses the Investbanq model, and AI CIO will ask again on your next question with investor or CRM data. Saving either option stops these prompts.',
                      )
                    : t(
                        'dataPolicy.noteLater',
                        'If you decide later, AI CIO will ask again on your next question with investor or CRM data. Saving either option stops these prompts.',
                      )}
              </NoteRow>
              <NoteRow icon={<History className="size-4" aria-hidden />}>
                {t(
                  'dataPolicy.noteHistory',
                  'You can change it anytime in Settings → Company. Every change is logged with your name.',
                )}
              </NoteRow>
            </>
          )}
        </ul>

        <a
          href={DATA_POLICY_HELP_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-foreground hover:underline"
        >
          {t('dataPolicy.help', 'How AI CIO handles investor data')}
          <ExternalLink className="size-4" aria-hidden />
        </a>

        {phase === 'save_failed' && (
          <AlertBox
            tone="error"
            icon={<CircleAlert className="size-4 text-red-600" aria-hidden />}
            title={t('dataPolicy.saveFailedTitle', 'Couldn’t save your choice')}
            body={
              paused
                ? t(
                    'dataPolicy.saveFailedBody',
                    'Something went wrong on our side. Nothing was saved and nothing was sent to {{provider}}. Try again, or decide later.',
                    { provider },
                  )
                : t(
                    'dataPolicy.saveFailedBodyGeneric',
                    'Something went wrong on our side. Nothing was saved. Try again, or decide later.',
                  )
            }
          />
        )}
        {!online && phase !== 'no_access' && (
          <AlertBox
            tone="warning"
            icon={<TriangleAlert className="size-4 text-amber-700" aria-hidden />}
            title={t('dataPolicy.offlineTitle', 'You’re offline')}
            body={
              paused
                ? t(
                    'dataPolicy.offlineBodyPaused',
                    'Reconnect to save your choice. Your request will wait, and nothing has been sent to {{provider}}.',
                    { provider },
                  )
                : t('dataPolicy.offlineBody', 'Reconnect to save your choice.')
            }
          />
        )}
        {noAccess && (
          <AlertBox
            tone="neutral"
            icon={<Lock className="size-4 text-foreground" aria-hidden />}
            title={t('dataPolicy.noAccessTitle', 'You no longer have admin access')}
            body={
              paused
                ? t(
                    'dataPolicy.noAccessBodyPaused',
                    'Your request will continue with the current data policy. Ask an admin if it needs to change.',
                  )
                : t('dataPolicy.noAccessBody', 'Ask an admin if the data policy needs to change.')
            }
          />
        )}
      </DialogScroll>

      <DialogActions key="choose">
        {noAccess ? (
          <Button
            className="min-w-[232px]"
            onClick={() => {
              resolve('later');
            }}
          >
            {t('dataPolicy.continue', 'Continue')}
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={decideLater} disabled={saving}>
              {manual
                ? t('dataPolicy.cancel', 'Cancel')
                : t('dataPolicy.decideLater', 'Decide later')}
            </Button>
            <Button
              className={cn('min-w-[232px]', !selected && 'opacity-50')}
              aria-disabled={!selected || saving || !online}
              disabled={saving || !online}
              onClick={() => {
                void submit();
              }}
            >
              {saving && <Loader2 className="animate-spin" aria-hidden />}
              {primaryLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
}

export function DataPolicyDialogHost() {
  const { t } = useTranslation('ai-cio');
  const request = usePolicyPromptStore((s) => s.request);
  const resolve = usePolicyPromptStore((s) => s.resolve);

  return (
    <Suspense fallback={null}>
      <Dialog
        open={request !== null}
        onOpenChange={(open) => {
          if (!open) {
            markCurrentPolicyNoteSeen();
            resolve('later');
          }
        }}
      >
        {request && (
          <DialogContent
            showClose={false}
            data-ai-cio-surface
            onEscapeKeyDown={(e) => {
              e.stopPropagation();
            }}
            overlayClassName="bg-black/40"
            onPointerDownOutside={(e) => {
              e.preventDefault();
            }}
            onInteractOutside={(e) => {
              e.preventDefault();
            }}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              (e.currentTarget as HTMLElement | null)
                ?.querySelector<HTMLElement>('h2[tabindex="-1"]')
                ?.focus();
            }}
            className="flex max-h-[calc(100vh-3rem)] w-[calc(100vw-2rem)] max-w-[720px] flex-col gap-0 overflow-hidden rounded-xl p-0"
          >
            <DataPolicyDialogBody key={request.id} request={request} />
            <button
              type="button"
              onClick={() => {
                markCurrentPolicyNoteSeen();
                resolve('later');
              }}
              aria-label={t('dataPolicy.close', 'Close and decide later')}
              className="absolute end-6 top-6 flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" aria-hidden />
            </button>
          </DialogContent>
        )}
      </Dialog>
    </Suspense>
  );
}
